import { useId } from "react";
import { DateText } from "@/components/DateText";
import { MoneyText } from "@/components/MoneyText";
import { MarketBadge } from "@/components/CarrierIdentity";
import {
  coverageRowsFor,
  partEconomicsRows,
  type CoverageDisplayPart,
  type CoverageRow,
} from "@/lib/submissions/coverageRows";

export function CoverageRows({
  rows,
  limits = false,
}: {
  rows: CoverageRow[];
  limits?: boolean;
}) {
  return (
    <dl className="flex min-w-0 flex-col gap-2">
      {rows.map((row) => (
        <div
          key={row.key}
          className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1"
        >
          <dt className="min-w-0 text-brk-dim">{row.label}</dt>
          <dd className="min-w-0 break-words text-right text-brk-text">
            {row.format === "money" ? (
              <MoneyText value={row.value} limit={limits} />
            ) : row.format === "date" ? (
              <DateText value={row.value} />
            ) : (
              row.value
            )}
            {row.qualifier && (
              <span className="ml-1 text-brk-dim">{row.qualifier}</span>
            )}
            {row.source === "requested" && (
              <span className="ml-1 inline-block text-xs text-brk-dim">
                As requested (not confirmed by carrier)
              </span>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}
export function TaxesAndFeesDetail({
  charges,
}: {
  charges: { label: string; amount: string }[];
}) {
  return charges.length ? (
    <details className="min-w-0 text-sm">
      <summary className="cursor-pointer text-brk-accent">
        Taxes and fees detail
      </summary>
      <dl className="mt-2 flex flex-col gap-2">
        {charges.map((c, i) => (
          <div
            key={i}
            className="flex flex-wrap items-baseline justify-between gap-x-3"
          >
            <dt className="text-brk-dim">{c.label}</dt>
            <dd className="text-brk-text">
              <MoneyText value={c.amount} />
            </dd>
          </div>
        ))}
      </dl>
    </details>
  ) : null;
}
export function CoveragePartSection({
  part,
  marketType,
  charges = [],
}: {
  part: CoverageDisplayPart;
  marketType?: "admitted" | "surplus_lines" | null;
  charges?: { label: string; amount: string }[];
}) {
  const id = useId();
  return (
    <section
      aria-labelledby={id}
      className="flex min-w-0 flex-col gap-3 rounded-md border border-brk-border p-3 text-sm"
    >
      <div className="flex flex-wrap items-center gap-2">
        <h4 id={id} className="text-subheading text-brk-text">
          {part.label}
        </h4>
        <MarketBadge type={part.marketType ?? marketType} />
      </div>
      {part.source === "requested" && (
        <p className="text-xs text-brk-dim">
          As requested (not confirmed by carrier)
        </p>
      )}
      <CoverageRows rows={partEconomicsRows(part)} />
      <CoverageRows rows={coverageRowsFor(part)} limits />
      <TaxesAndFeesDetail charges={charges} />
    </section>
  );
}
